import { FC, useCallback, useEffect, useState } from 'react';
import React from 'react';

import { Empty } from 'antd';
import { useParams } from 'react-router-dom';
import { Button, Label, Typography } from 'ui';
import BackLink from 'ui/BackLink';

import { activeEventNameSelector } from 'selectors/eventsSlice.selector';
import {
  useCreateScheduleMutation,
  useDeleteScheduleMutation,
  useLazyGetSchedulesQuery,
  useUpdateScheduleMutation,
} from 'services/promoter/promoterApiService';
import { useAppSelector } from 'store/index';

import EventsLoadingState from 'containers/events-loading-state';

import { EventScheduleReqType } from 'types/promoters/promoterTypes';

import s from './EventSchedule.module.scss';
import ScheduleForm from './scheduleForm';
import ScheduleRow from './scheduleRow';

const EventSchedule: FC = () => {
  const { eventId } = useParams();
  const [schedules, setSchedules] = useState<EventScheduleReqType[]>([]);
  const [addingSchedule, setAddingSchedule] = useState(false);
  const [editingScheduleIndex, setEditingScheduleIndex] = useState<number | null>(null);
  const eventName = useAppSelector(activeEventNameSelector);
  const [actingId, setActingId] = useState<string | undefined>();
  const [createSchedule, { isLoading: creating }] = useCreateScheduleMutation();
  const [updateSchedule, { isLoading: updating }] = useUpdateScheduleMutation();
  const [getSchedules, { isFetching }] = useLazyGetSchedulesQuery();
  const [deleteSchedule, { isLoading: deleting }] = useDeleteScheduleMutation();

  const handleSubmitSchedule = (newSchedule: EventScheduleReqType) => {
    if (editingScheduleIndex !== null) {
      const updated = [...schedules];
      updated[editingScheduleIndex] = newSchedule;
      setSchedules(updated);
    } else {
      setSchedules((prev) => [...prev, newSchedule]);
    }

    // Reset
    setEditingScheduleIndex(null);
    setAddingSchedule(false);
  };

  const handleSaveData = async (schedule: EventScheduleReqType, id?: string) => {
    if (id) {
      const res = await updateSchedule({ id: id, data: schedule });
      if ('data' in res) {
        setSchedules(res?.data?.schedules);
      }
      setActingId(id);
    } else {
      const res = await createSchedule(schedule);
      if ('data' in res) {
        setSchedules(res?.data?.schedules);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (id) {
      setActingId(id);
      const res = await deleteSchedule(id);
      if ('data' in res) {
        setSchedules(res?.data?.schedules);
      }
    }
  };

  const handleFetch = useCallback(
    async (id: string) => {
      if (!id) return;

      const res = await getSchedules({ eventId: id });
      if ('data' in res) {
        setSchedules(res.data?.schedules ?? []);
      }
    },
    [getSchedules],
  ); // Include getSchedules if it's a stable function (e.g., from Redux RTK Query or useAPI())

  useEffect(() => {
    if (eventId) {
      handleFetch(eventId);
    }
  }, [eventId, handleFetch]);

  return (
    <div className={s.wrapper}>
      <BackLink />
      <div className={s.header}>
        <div className={s.titleHeader}>
          <Typography type="h2" weight={700}>
            Recurent Schedules
          </Typography>
        </div>
        {eventName && <Typography type="h4">{eventName}</Typography>}
      </div>
      <div className={s.rowWrapper}>
        {!!schedules.length && (
          <div className={s.labelWrapper}>
            <Label>FRIENDLY NAME</Label>
            <Label>FROM</Label>
            <Label>TO</Label>
            <Label>ACTIVE?</Label>
            <Label>ACTION</Label>
          </div>
        )}
        {schedules.map((schedule, idx) => (
          <React.Fragment key={schedule.id}>
            <ScheduleRow
              isLoading={creating || updating}
              deleting={deleting}
              schedule={schedule}
              actingId={actingId}
              onUpdate={(updated) => {
                const updatedList = [...schedules];
                updatedList[idx] = updated;
                setSchedules(updatedList);
              }}
              onEdit={() => {
                setEditingScheduleIndex(idx);
                setAddingSchedule(true);
              }}
              onSave={(id) => {
                setEditingScheduleIndex(idx);
                handleSaveData(schedules[idx], id);
              }}
              onDelete={(id) => {
                handleDelete(id ?? '');
                setAddingSchedule(false);
              }}
            />

            {editingScheduleIndex === idx && addingSchedule && (
              <ScheduleForm
                initial={schedules[idx]}
                onSubmit={handleSubmitSchedule}
                onCancel={() => {
                  setEditingScheduleIndex(null);
                  setAddingSchedule(false);
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      {!addingSchedule && (
        <div
          style={{
            display: 'flex',
            justifyContent: schedules.length === 0 ? 'center' : 'flex-end',
            alignItems: schedules.length === 0 ? 'center' : 'unset',
            height: schedules.length === 0 ? '60vh' : 'auto',
            marginTop: schedules.length > 0 ? 24 : 0,
          }}>
          {isFetching ? (
            <EventsLoadingState size={240} />
          ) : (
            <div style={{ textAlign: 'center', width: 160 }}>
              {schedules.length === 0 && <Empty description="No schedules." />}
              <Button
                style={{ width: '100%', marginTop: schedules.length === 0 ? 24 : 0 }}
                onClick={() => {
                  setAddingSchedule(true);
                  setEditingScheduleIndex(null);
                }}>
                ADD SCHEDULE
              </Button>
            </div>
          )}
        </div>
      )}

      {addingSchedule && editingScheduleIndex === null && (
        <ScheduleForm
          onSubmit={handleSubmitSchedule}
          onCancel={() => {
            setEditingScheduleIndex(null);
            setAddingSchedule(false);
          }}
        />
      )}
    </div>
  );
};

export default EventSchedule;
